import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, StatusPill } from "@playbookai/ui";
import type { TeamRequest, TeamRequestAction } from "../../api/teams-api.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { presentTeamRequest, type RequestQueue } from "./request-direction.js";
import { PersonInitials, TeamTile } from "./team-identity.js";

export interface QueuedRequest {
  readonly queue: RequestQueue;
  readonly row: TeamRequest;
}

/**
 * One list of pending requests, drawn the same way on the Requests view and on
 * a team's Pending tab: who, what, when, and what the reader may do about it.
 *
 * `presentTeamRequest` decides the sentence and the actions from (queue, kind),
 * so this component never re-derives who the reader is — see that module for
 * why the queue alone is enough, and why an owner's own invitation is not a
 * request to join.
 *
 * NO ROW IS ACCENTED (DS invariant §9). Accept and Approve are `secondary`,
 * Decline and Withdraw are `ghost`: a queue of five invitations with five
 * indigo buttons is five accents in one region.
 */
export function TeamRequestList({
  rows,
  pending,
  onResolve,
  showTeamLink = true,
  emptyMessage,
}: {
  rows: readonly QueuedRequest[];
  pending: boolean;
  onResolve: (requestId: string, action: TeamRequestAction) => void;
  /** Off on a team's own page, where every row is about that team. */
  showTeamLink?: boolean;
  /** Said inside the list frame when there are no rows. */
  emptyMessage: ReactNode;
}): ReactElement {
  const format = useDateTimeFormatter();
  /* Kept hand-built: Card is the 14px corner with an 80% border and the top
     hairline (today 10px on the full pb-border), List would restyle the rows,
     and EmptyState size="sm" sets 11px type; no PR 3 look row approves
     these. */
  return (
    <div className="rounded-lg border border-pb-border bg-pb-card">
      {rows.length === 0 ? (
        <p className="px-4 py-5 text-body-small text-pb-text-muted">
          {emptyMessage}
        </p>
      ) : (
        <ul className="divide-y divide-pb-border">
          {rows.map(({ queue, row }) => {
            const { headline, subject, actions } = presentTeamRequest(
              queue,
              row,
            );
            const when = format.relative(row.createdAt);
            return (
              <li
                key={row.requestId}
                className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3"
              >
                {subject.kind === "team" ? (
                  <TeamTile name={subject.name} />
                ) : (
                  <PersonInitials name={subject.name} seed={subject.id} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-body text-pb-text">{headline}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-body-small text-pb-text-muted">
                    {when ? <span>{when}</span> : null}
                    {when && showTeamLink ? (
                      <span aria-hidden="true">·</span>
                    ) : null}
                    {/* Kept: Link variant="entity" adds weight 500 and the
                        ring focus, a look change no PR 3 row approves. */}
                    {showTeamLink ? (
                      <Link
                        to={`/teams/${row.teamId}`}
                        className="focus-ring rounded-sm text-pb-accent-text underline-offset-2 hover:underline"
                      >
                        Open {row.teamName}
                      </Link>
                    ) : null}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/* An ARCHIVED team accepts nobody, and the server refuses —
                      say so here rather than offering a button that cannot
                      work. */}
                  {row.teamArchived ? (
                    <StatusPill tone="watch">Team archived</StatusPill>
                  ) : (
                    actions.map(({ action, label, variant }) => (
                      <Button
                        key={action}
                        size="sm"
                        {...(variant ? { variant } : {})}
                        disabled={pending}
                        onClick={() => onResolve(row.requestId, action)}
                      >
                        {label}
                      </Button>
                    ))
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
